import React from 'react';
import ContentLoader from 'react-content-loader';
import { between, down, only, up } from 'styled-breakpoints';
import styled from 'styled-components';

import HeaderPlaceholder from '@rebound/design-system/components/HeaderPlaceholder';

const InfoCard = () => (
  <InfoCardWrapper>
    <InfoCardContent>
      <rect x="0" y="0" rx="0" ry="0" width="200" height="26" />
      <rect x="0" y="42" rx="0" ry="0" width="350" height="20" />
      <rect x="0" y="70" rx="0" ry="0" width="150" height="20" />
      <rect x="0" y="124" rx="0" ry="0" width="60" height="20" />
    </InfoCardContent>
  </InfoCardWrapper>
);

const CarrierServiceCardPlaceholder = () => (
  <>
    <Wrapper>
      <HeaderPlaceholder />
      <ContentWrapper>
        <MainContent>
          <MainRowItem>
            <TextContentRow>
              <FormWrapper>
                <DesktopForm height="260" width="100%">
                  <rect x="0" y="0" rx="0" ry="0" width="150" height="32" />
                  <rect x="0" y="36" rx="0" ry="0" width="250" height="32" />
                  <rect x="0" y="90" rx="0" ry="0" width="500" height="16" />
                  <rect x="0" y="110" rx="0" ry="0" width="250" height="16" />
                  <rect x="0" y="156" rx="0" ry="0" width="373" height="38" />
                  <rect x="0" y="200" rx="0" ry="0" width="373" height="50" />
                </DesktopForm>
                <TabletForm height="146" width="100%">
                  <rect x="0" y="0" rx="0" ry="0" width="350" height="32" />
                  <rect x="0" y="52" rx="0" ry="0" width="700" height="16" />
                  <rect x="0" y="108" rx="0" ry="0" width="400" height="38" />
                  <rect x="420" y="108" rx="0" ry="0" width="170" height="38" />
                </TabletForm>
              </FormWrapper>
            </TextContentRow>
            <ImagesRow>
              <div>
                <TopLeftImageWrapper>
                  <ContentLoader width="275" height="136">
                    <rect x="0" y="0" rx="0" ry="0" width="275" height="136" />
                  </ContentLoader>
                </TopLeftImageWrapper>
                <BottomLeftImageWrapper>
                  <ContentLoader width="275" height="440">
                    <rect x="0" y="0" rx="0" ry="0" width="275" height="440" />
                  </ContentLoader>
                </BottomLeftImageWrapper>
              </div>
              <div>
                <TopRightImageWrapper>
                  <ContentLoader width="275" height="360">
                    <rect x="0" y="0" rx="0" ry="0" width="275" height="360" />
                  </ContentLoader>
                </TopRightImageWrapper>
                <BottomRightImageWrapper>
                  <ContentLoader width="275" height="342">
                    <rect x="0" y="0" rx="0" ry="0" width="275" height="342" />
                  </ContentLoader>
                </BottomRightImageWrapper>
              </div>
            </ImagesRow>
            <ImagesRowTable>
              <ContentLoader width="100%" height="342">
                <rect x="0" y="0" rx="0" ry="0" width="1300" height="342" />
              </ContentLoader>
            </ImagesRowTable>
          </MainRowItem>
        </MainContent>
        <InformationCardsWrapper>
          <InfoCard />
          <InfoCard />
        </InformationCardsWrapper>
      </ContentWrapper>
      <div>
        <Footer>
          <FooterCopyright>
            <rect x="0" y="0" rx="0" ry="0" width="100%" height="20" />
          </FooterCopyright>
          <FooterLinkWrapper>
            <ContentLoader width="144" height="20">
              <rect x="0" y="0" rx="0" ry="0" width="144" height="20" />
            </ContentLoader>
            <ContentLoader width="96" height="20">
              <rect x="0" y="0" rx="0" ry="0" width="96" height="20" />
            </ContentLoader>
          </FooterLinkWrapper>
        </Footer>
      </div>
    </Wrapper>
  </>
);

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${(props) => props.theme.colors.grayLight};
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px 20px;
`;

const MainContent = styled.div`
  padding: 24px 0;
  display: flex;
  align-items: center;

  ${up('sm')} {
    align-items: flex-start;
    justify-content: center;
    padding: 64px 0 0;
    min-height: calc(100vh - 550px);
  }

  ${up('lg')} {
    align-items: center;
    padding: 0 140px;
  }

  ${between('sm', 'md')} {
    min-height: auto;
  }
`;

const MainRowItem = styled.div`
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;

  ${up('lg')} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;

const TextContentRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${up('sm')} {
    margin-bottom: 48px;
  }

  ${up('lg')} {
    display: grid;
    grid-template-columns: 1fr;
    justify-items: center;
  }

  ${only('md')} {
    min-width: 760px;
  }
`;

const FormWrapper = styled.div`
  max-width: 370px;

  ${up('sm')} {
    width: auto;
    max-width: 100%;
  }

  ${up('lg')} {
    max-width: 370px;
  }
`;

const TabletForm = styled(ContentLoader)`
  display: none;

  ${up('sm')} {
    display: block;
  }

  ${up('lg')} {
    display: none;
  }
`;

const DesktopForm = styled(ContentLoader)`
  display: block;

  ${up('sm')} {
    display: none;
  }

  ${up('lg')} {
    display: block;
  }
`;

const ImagesRow = styled.div`
  display: flex;
  margin-left: auto;

  ${down('md')} {
    display: none;
  }
`;

const TopLeftImageWrapper = styled.div`
  margin-top: 50px;
  padding: 9px;
  box-shadow: border-box;
`;

const BottomLeftImageWrapper = styled.div`
  padding: 9px;
  box-shadow: border-box;
`;

const TopRightImageWrapper = styled.div`
  padding: 9px;
  box-shadow: border-box;
`;

const BottomRightImageWrapper = styled.div`
  padding: 9px;
  box-shadow: border-box;
`;

const ImagesRowTable = styled.div`
  display: none;

  ${up('sm')} {
    display: flex;
  }

  ${up('lg')} {
    display: none;
  }

  ${between('sm', 'md')} {
    width: calc(100% + 40px);
    transform: translateX(-20px);
  }
`;

const InformationCardsWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;

  ${down('sm')} {
    flex-direction: column;
    align-items: center;
  }

  ${between('sm', 'md')} {
    margin-top: -25px;
    position: relative;
    z-index: 2;
  }

  ${up('sm')} {
    flex-direction: row;
    align-items: stretch;
  }
`;

const InfoCardWrapper = styled.div`
  max-width: 430px;
  padding: 18px 16px 10px;
  width: 100%;
  background-color: ${(props) => props.theme.colors.white};

  ${down('sm')} {
    margin: 12px 20px;
    width: auto;
  }

  ${up('sm')} {
    margin: 0 10px;
  }
`;

const InfoCardContent = styled(ContentLoader)`
  max-width: 100%;
  width: 100%;
`;

const Footer = styled.div`
  background: ${(props) => props.theme.colors.white};
  padding: 19px 20px 24px;
  width: 100%;
  display: block;
  box-sizing: border-box;
  border-top: 1px solid ${(props) => props.theme.colors.grayDark2};

  ${up('sm')} {
    padding: 16px 10px;
    display: flex;
    justify-content: space-between;
  }

  ${up('lg')} {
    padding: 26px 140px;
  }
`;

const FooterLinkWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${up('sm')} {
    flex-direction: row;
  }

  & > *:not(:last-child) {
    margin-bottom: 15px;
  }

  ${up('sm')} {
    & > *:not(:last-child) {
      margin-bottom: 0;
      margin-right: 15px;
    }
  }
`;

const FooterCopyright = styled(ContentLoader)`
  width: 100%;
  height: 20px;
  margin-bottom: 12px;

  ${up('sm')} {
    width: 50%;
    margin-bottom: 0;
  }
`;

export default CarrierServiceCardPlaceholder;
